import React from 'react';
import { Link } from 'react-router-dom';
import { useSystemSettings } from '../hooks/useSystemSettings';

const ProjectCard = ({ project, onDragStart, onUpdate, isDragging }) => {
  const { getIndustryLabel } = useSystemSettings();
  
  const formatCurrency = (amount, currency = 'KZT') => {
    if (!amount) return 'Не указано';
    
    if (currency === 'KZT') {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
    } else if (currency === 'USD') {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
    } else if (currency === 'RUB') {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ₽';
    } else {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ' + currency;
    }
  };

  const readinessLabels = {
    concept: 'Концепт',
    design: 'Проектирование',
    construction_start: 'Начало строительства',
    active_construction: 'Активная стройка',
    approaching_em: 'Подходит к ЭМ',
    completion: 'Завершение'
  };

  const flagIcons = {
    hot: '🔥',
    paused: '⏸️',
    risk: '⚠️'
  };

  const flagColors = {
    hot: 'bg-orange-100 text-orange-800 border-orange-200',
    paused: 'bg-gray-100 text-gray-800 border-gray-200',
    risk: 'bg-red-100 text-red-800 border-red-200',
    waiting_construction: 'bg-blue-100 text-blue-800 border-blue-200',
    waiting_funding: 'bg-cyan-100 text-cyan-800 border-cyan-200'
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, project)}
      className={`project-card ${project.flags?.includes('hot') ? 'hot' : ''} ${isDragging ? 'dragging' : ''}`}
      data-testid={`project-card-${project.id}`}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <h4 className="font-semibold text-gray-900 text-sm leading-tight flex-1 mr-2">
          {project.name}
        </h4>
        <div className="flex gap-1">
          {project.flags?.map((flag) => (
            <span
              key={flag}
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${flagColors[flag] || 'bg-gray-100 text-gray-800'}`}
              title={flag}
            >
              {flagIcons[flag]} {flag === 'hot' ? 'Горячий' : flag === 'paused' ? 'Пауза' : 'Риск'}
            </span>
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center text-xs text-gray-600">
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {project.city}
        </div>
        
        <div className="flex items-center text-xs text-gray-600">
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0h6" />
          </svg>
          {getIndustryLabel(project.industry)}
        </div>

        {project.customer_company && (
          <div className="flex items-center text-xs text-gray-600">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {project.customer_company}
          </div>
        )}

        <div className="flex items-center text-xs text-gray-600">
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {readinessLabels[project.readiness_phase] || project.readiness_phase}
        </div>
      </div>

      {/* Budget */}
      {project.budget_amount && (
        <div className="mb-3 p-2 bg-green-50 rounded border border-green-200">
          <div className="flex items-center text-sm font-semibold text-green-800">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            {formatCurrency(project.budget_amount, project.budget_currency)}
          </div>
        </div>
      )}

      {/* Timeline */}
      {(project.build_start_year || project.build_end_year) && (
        <div className="mb-3 text-xs text-gray-600">
          <div className="flex items-center">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {project.build_start_year && project.build_end_year
              ? `${project.build_start_year} - ${project.build_end_year}`
              : project.build_start_year
              ? `От ${project.build_start_year}`
              : `До ${project.build_end_year}`
            }
          </div>
        </div>
      )}

      {/* Notes */}
      {project.notes && (
        <div className="mb-3 p-2 bg-blue-50 rounded text-xs text-blue-800 border border-blue-200">
          <div className="flex items-start">
            <svg className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="break-words">{project.notes.length > 60 ? `${project.notes.substring(0, 60)}...` : project.notes}</span>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-between items-center pt-2 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          {new Date(project.updated_at).toLocaleDateString('ru-RU')}
        </div>
        <Link
          to={`/projects/${project.id}`}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
          data-testid={`view-project-${project.id}`}
        >
          Подробнее →
        </Link>
      </div>
    </div>
  );
};

export default ProjectCard;